import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { backboneListColumns, type BackboneService } from "./BackboneData";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  type FilterField,
  labelId,
  parseToken,
  type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeBackboneFields } from "./backboneFieldSpec";
import {
  fetchBackboneList,
  mapBbaasItemToBackboneService,
} from "./requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { DataStateView } from "./BackboneDetails/StateViews/DataStateView";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";

const MainBackbone: React.FC = () => {
  const navigate = useNavigate();
  const api = useAxiosAuth0();
  
  const { tenantId: selectionTenantId, partnerId } = useAppSelector(
    (state) => state.selection
  );
  const tenantId = selectionTenantId ?? null;
  const isViewingAllCustomers =
    selectionTenantId != null && selectionTenantId === partnerId;

  const [backboneList, setBackboneList] = useState<BackboneService[]>([]);
  const [isFetchingList, setIsFetchingList] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [offset, setOffset] = useState(0);
  const limit = 10;
  const [pagination, setPagination] = useState<{
    total: number;
    hasMore: boolean;
  }>({ total: 0, hasMore: false });

  const prevTenantIdForFetch = useRef(selectionTenantId);

  const loadData = useCallback(async () => {
    try {
      setIsFetchingList(true);
      setFetchError(null);
      if (tenantId == null) {
        setBackboneList([]);
        setPagination({ total: 0, hasMore: false });
        return;
      }
      const response = await fetchBackboneList(api, tenantId, {
        limit,
        offset,
      });
      const { status, data: body } = response;

      if (status >= 500 || (body && body.success === false)) {
        setFetchError(body?.message ?? "Unknown server error");
        setBackboneList([]);
        setPagination({ total: 0, hasMore: false });
      } else if (body?.success && Array.isArray(body.data)) {
        setBackboneList(body.data.map(mapBbaasItemToBackboneService));
        setPagination({
          total: body.pagination?.total ?? body.data.length,
          hasMore: body.pagination?.hasMore ?? false,
        });
      } else {
        setBackboneList([]);
        setPagination({ total: 0, hasMore: false });
      }
    } catch (err) {
      console.error("Backbone list fetch failed", err);
      setFetchError(
        err && typeof err === "object" && "message" in err
          ? String((err as { message: string }).message)
          : "Failed to load backbones"
      );
      setBackboneList([]);
      setPagination({ total: 0, hasMore: false });
    } finally {
      setIsFetchingList(false);
      setHasFetched(true);
    }
  }, [api, tenantId, offset]);

  const showInitialOverlay =
    !hasFetched || (isFetchingList && backboneList.length === 0);
  const overlaySpinner = useProcessingOverlay(showInitialOverlay);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (prevTenantIdForFetch.current !== selectionTenantId) {
      prevTenantIdForFetch.current = selectionTenantId;
      setHasFetched(false);
    }
  }, [selectionTenantId]);

  const backboneFields = useMemo(
    () => makeBackboneFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const suggestedKeys = useMemo(() => {
    const headers = backboneListColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];
    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(0, 0, "Customer");
    }
    return headers;
  }, [isViewingAllCustomers]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<BackboneService>>();
    for (const f of backboneFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [backboneFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of backboneFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [backboneFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<BackboneService>(normalizedFilters, backboneFields),
    [normalizedFilters, backboneFields]
  );

  const filteredData = useMemo(
    () => backboneList.filter(predicate),
    [backboneList, predicate]
  );

  const handleBackboneIdClick = useCallback(
    (row: BackboneService) => {
      navigate(`/kontrol/backbone-details?backboneId=${row.id}`, {
        state: { selectedBackboneRow: row },
      });
    },
    [navigate]
  );

  const columns = useMemo<GenericTableColumn<BackboneService>[]>(() => {
    const cols = [...backboneListColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: BackboneService) => row.customer?.name ?? "",
        renderCell: (row: BackboneService) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId || "-"}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }
    return cols.map((col) => {
      if (col.header === "Backbone ID") {
        return {
          ...col,
          renderCell: (row: BackboneService) => (
            <button
              type="button"
              onClick={() => handleBackboneIdClick(row)}
              className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
            >
              {row.subscriptionId ?? row.id}
            </button>
          ),
        };
      }
      return col;
    });
  }, [handleBackboneIdClick, isViewingAllCustomers]);

  const isDemo = isDemoClient();
  const backboneColumnVisibility = useColumnVisibility(
    "kontrol:backbone:columns",
    columns,
    ["Backbone ID"],
  );
  const tableColumns = isDemo ? backboneColumnVisibility.visibleColumns : columns;

  const tableColumnCount =
    backboneListColumns.length + (isViewingAllCustomers ? 1 : 0);

  if (showInitialOverlay) {
    return <>{overlaySpinner}</>;
  }
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  if (backboneList.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative p-4 overflow-hidden flex flex-col h-full">
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              {backboneList.length > 0 && (
                <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                  <div className="flex items-center justify-between gap-3 my-5">
                    <div className="w-full max-w-full sm:max-w-[500px]">
                      <GenericSearchbar
                        placeholder="Find by attribute or value"
                        suggestedKeys={suggestedKeys}
                        onFiltersChange={setActiveFilters}
                      />
                    </div>
                    {isDemo && (
                      <ColumnVisibilityControl
                        columns={columns}
                        isVisible={backboneColumnVisibility.isVisible}
                        toggle={backboneColumnVisibility.toggle}
                        requiredIds={["Backbone ID"]}
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="flex-1 min-h-0 flex flex-col">
                <DataStateView
                  loading={isFetchingList && backboneList.length > 0}
                  error={null}
                  isEmpty={false}
                  onRetry={loadData}
                  loadingContent={
                    <TableSkeleton columns={tableColumnCount} rows={8} />
                  }
                >
                  <div className="flex flex-col h-full min-h-0">
                    <GenericTable<BackboneService>
                      columns={tableColumns}
                      data={filteredData}
                      initialRowsPerPage={10}
                      rowsPerPageOptions={[10, 20, 30]}
                      width="100%"
                      tableClassName="bg-semantic-bg-primary"
                      maxTableBodyHeight="100%"
                    />
                    {pagination.total > 0 && (
                      <div className="flex items-center justify-between mt-2 py-2 border-t border-semantic-border-primary flex-shrink-0">
                        <span className="text-xs text-semantic-text-secondary">
                          {pagination.total} result{pagination.total !== 1 ? "s" : ""}
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={offset === 0 || isFetchingList}
                            onClick={() => setOffset((o) => Math.max(0, o - limit))}
                            className="px-2 py-1 rounded text-xs font-medium bg-semantic-bg-primary text-semantic-text-primary border border-semantic-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-semantic-state-active"
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            disabled={!pagination.hasMore || isFetchingList}
                            onClick={() => setOffset((o) => o + limit)}
                            className="px-2 py-1 rounded text-xs font-medium bg-semantic-bg-primary text-semantic-text-primary border border-semantic-border-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-semantic-state-active"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </DataStateView>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MainBackbone;
